import { Link } from 'react-router-dom';

export function Landing(){
  return (
    <div className="min-h-screen flex flex-col bg-slate-950 text-slate-50">
      <Header />
      <main>
        <Hero />
        <TrustBadges />
        <HowItWorks />
        <FeatureGrid />
        <UseCases />
        <Testimonials />
        <AboutCreator />
        <FAQ />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}

function Header(){
  return (
    <header className="sticky top-0 z-50 backdrop-blur-lg bg-slate-950/80 border-b border-slate-800/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <i className="fa-solid fa-wand-magic-sparkles text-white text-sm"></i>
            </div>
            <span className="font-bold text-lg">CopyGenius</span>
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-medium px-4 py-2 hover:text-brand-400 transition-colors"
            >
              Entrar
            </Link>
            <Link
              to="/register"
              className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-400 text-white transition-all hover:scale-105 shadow-lg"
            >
              Começar Grátis
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

function Shell({children}:{children:React.ReactNode}){
  return <div className="mx-auto w-full max-w-6xl px-6">{children}</div>;
}

function Hero(){
  return (
    <section className="relative overflow-hidden pt-24 pb-32 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" aria-label="Hero Section">
      <Shell>
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-300 text-xs font-medium mb-6">
            <i className="fa-solid fa-sparkles"></i>
            Powered by IA avançada.
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-50 leading-tight">
            Pare de perder vendas por<br />
            <span className="bg-gradient-to-r from-brand-400 via-brand-300 to-brand-500 bg-clip-text text-transparent">
              falta de copy persuasiva
            </span>
          </h1>

          <p className="mt-8 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Crie textos que vendem para e-commerce, redes sociais e campanhas em 10 segundos.
            A IA faz o trabalho pesado enquanto você foca em escalar seu negócio.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/register" className="btn btn-primary h-14 px-8 text-base shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
              <i className="fa-solid fa-rocket"></i> Começar Grátis
            </Link>
            <Link to="/login" className="btn btn-outline h-14 px-8 text-base hover:bg-slate-800/50">
              <i className="fa-solid fa-right-to-bracket"></i> Fazer Login
            </Link>
          </div>

          <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-check-circle text-green-400"></i>
              <span>Sem cartão de crédito</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-check-circle text-green-400"></i>
              <span>Créditos gratuitos</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-check-circle text-green-400"></i>
              <span>Resultados em 10 segundos</span>
            </div>
          </div>
        </div>
      </Shell>

      {/* Background Effects */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[800px] h-[800px] bg-brand-500/10 rounded-full blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl" aria-hidden="true" />
    </section>
  );
}

function TrustBadges(){
  return (
    <section className="py-8 border-b border-slate-800/40" aria-label="Selos de Confiança">
      <Shell>
        <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12 text-slate-400">
          <div className="flex items-center gap-2 text-sm">
            <i className="fa-solid fa-shield-halved text-green-400"></i>
            <span>Dados Protegidos</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <i className="fa-solid fa-lock text-green-400"></i>
            <span>SSL Seguro</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <i className="fa-solid fa-credit-card text-green-400"></i>
            <span>Sem Cartão para Testar</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <i className="fa-solid fa-star text-yellow-400"></i>
            <span>4.8/5 Avaliação</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <i className="fa-brands fa-instagram text-pink-400"></i>
            <span>@copygenius.ia</span>
          </div>
        </div>
      </Shell>
    </section>
  );
}

function HowItWorks(){
  return (
    <section className="py-24 bg-slate-900/50" aria-label="Como Funciona" id="como-funciona">
      <Shell>
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-50 mb-4">
            Como criar copy profissional em 3 passos
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Simples, rápido e sem complicação. Veja como funciona:
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          <article className="relative bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/60 p-8 text-center hover:border-brand-500/40 transition-all">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-sm">
              1
            </div>
            <div className="w-16 h-16 mx-auto rounded-xl bg-brand-500/10 flex items-center justify-center text-brand-400 text-3xl mb-6">
              <i className="fa-solid fa-user-plus"></i>
            </div>
            <h3 className="font-bold text-slate-100 text-xl mb-3">Crie sua conta grátis</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Cadastre-se em segundos. Não precisa de cartão de crédito e você já ganha créditos para testar.
            </p>
          </article>

          <article className="relative bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/60 p-8 text-center hover:border-brand-500/40 transition-all">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-sm">
              2
            </div>
            <div className="w-16 h-16 mx-auto rounded-xl bg-brand-500/10 flex items-center justify-center text-brand-400 text-3xl mb-6">
              <i className="fa-solid fa-pen-to-square"></i>
            </div>
            <h3 className="font-bold text-slate-100 text-xl mb-3">Descreva seu produto</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Informe o nome, características e público-alvo. A IA entende o contexto e cria textos personalizados.
            </p>
          </article>

          <article className="relative bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/60 p-8 text-center hover:border-brand-500/40 transition-all">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-sm">
              3
            </div>
            <div className="w-16 h-16 mx-auto rounded-xl bg-brand-500/10 flex items-center justify-center text-brand-400 text-3xl mb-6">
              <i className="fa-solid fa-rocket"></i>
            </div>
            <h3 className="font-bold text-slate-100 text-xl mb-3">Receba copy pronta</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Em 10 segundos, receba títulos, descrições e CTAs profissionais prontos para copiar e usar.
            </p>
          </article>
        </div>

        <div className="mt-12 text-center">
          <Link to="/register" className="btn btn-primary h-12 px-8 text-base">
            <i className="fa-solid fa-rocket"></i> Começar Agora - É Grátis
          </Link>
        </div>
      </Shell>
    </section>
  );
}

const TESTIMONIALS = [
  {
    name: 'Maria Silva',
    role: 'Loja de Roupas Online',
    avatar: 'M',
    rating: 5,
    text: 'Excelente ferramenta! Economizo horas criando descrições para minha loja. As copies são persuasivas e prontas para usar. Minha conversão aumentou 30%!'
  },
  {
    name: 'João Santos',
    role: 'Social Media Manager',
    avatar: 'J',
    rating: 5,
    text: 'Perfeito para quem trabalha com social media. Gero legendas criativas em segundos para meus clientes. Indispensável no meu dia a dia.'
  },
  {
    name: 'Ana Costa',
    role: 'Empreendedora Digital',
    avatar: 'A',
    rating: 5,
    text: 'Testei várias ferramentas de IA e o CopyGenius é disparado a melhor para português brasileiro. Entende o contexto e cria textos que realmente vendem.'
  },
  {
    name: 'Pedro Oliveira',
    role: 'Dono de E-commerce',
    avatar: 'P',
    rating: 5,
    text: 'Cadastrei 200 produtos em 2 dias usando o CopyGenius. Sozinho demoraria semanas. O melhor investimento que fiz para meu negócio.'
  }
];

function Testimonials(){
  return (
    <section className="py-24 bg-slate-950" aria-label="Depoimentos de Clientes" id="depoimentos">
      <Shell>
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-50 mb-4">
            O que nossos usuários dizem
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Mais de 150 empreendedores já usam CopyGenius para criar textos que vendem
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {TESTIMONIALS.map((t, i) => (
            <article key={i} className="bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/60 p-6 hover:border-brand-500/40 transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-brand-500/20 flex items-center justify-center text-brand-400 font-bold text-lg flex-shrink-0">
                  {t.avatar}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-100">{t.name}</div>
                  <div className="text-sm text-slate-400">{t.role}</div>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(t.rating)].map((_, j) => (
                    <i key={j} className="fa-solid fa-star text-yellow-400 text-sm"></i>
                  ))}
                </div>
              </div>
              <blockquote className="text-slate-300 text-sm leading-relaxed italic">
                "{t.text}"
              </blockquote>
            </article>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-slate-900/80 border border-slate-700/60">
            <div className="flex -space-x-2">
              {['M', 'J', 'A', 'P'].map((l, i) => (
                <div key={i} className="w-8 h-8 rounded-full bg-brand-500/20 border-2 border-slate-900 flex items-center justify-center text-brand-400 text-xs font-bold">
                  {l}
                </div>
              ))}
            </div>
            <div className="text-sm">
              <span className="font-semibold text-slate-100">150+ usuários</span>
              <span className="text-slate-400"> confiam no CopyGenius</span>
            </div>
          </div>
        </div>
      </Shell>
    </section>
  );
}

function FeatureGrid(){
  return (
    <section className="py-24 bg-slate-900/30" aria-label="Funcionalidades" id="funcionalidades">
      <Shell>
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-50 mb-4">
            Tudo que você precisa para criar copy de alto impacto
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Ferramentas inteligentes que eliminam o bloqueio criativo e aceleram seus resultados
          </p>
        </div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(f => (
            <div key={f.title} className="group relative bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/60 p-7 hover:border-brand-500/60 hover:bg-slate-900 transition-all hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white text-xl shadow-lg mb-5 group-hover:scale-110 transition-transform">
                <i className={`fa-solid ${f.icon}`}></i>
              </div>
              <h3 className="font-bold text-slate-100 text-lg mb-3">{f.title}</h3>
              <p className="text-sm leading-relaxed text-slate-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </Shell>
    </section>
  );
}

const FEATURES = [
  { title:'Especialistas IA', desc:'Modes específicos para e-commerce e social media, com outputs estruturados e prontos para usar.', icon:'fa-robot' },
  { title:'Copy Estruturada', desc:'Títulos, descrições e bullets organizados automaticamente - fácil de ler e copiar.', icon:'fa-list-check' },
  { title:'Resultados Rápidos', desc:'Gere conteúdo profissional em 10 segundos. Sem espera, sem complexidade.', icon:'fa-bolt' },
  { title:'Múltiplas Variações', desc:'Receba diversas opções de títulos e abordagens para testar e escolher a melhor.', icon:'fa-layer-group' },
  { title:'Histórico Completo', desc:'Acesse todas as gerações anteriores quando precisar revisar ou reutilizar.', icon:'fa-clock-rotate-left' },
  { title:'Seguro e Privado', desc:'Seus dados e conteúdos protegidos com infraestrutura profissional.', icon:'fa-shield-halved' },
];

function UseCases(){
  return (
    <section className="py-24 bg-slate-900/50" aria-label="Casos de Uso">
      <Shell>
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-50 mb-4">
            Feito para quem quer vender mais, não perder tempo
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Seja você empreendedor solo, agência ou time de marketing
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div className="bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/60 p-8 hover:border-brand-500/40 transition-all">
            <div className="w-12 h-12 rounded-xl bg-brand-500/10 flex items-center justify-center text-brand-400 text-2xl mb-5">
              <i className="fa-solid fa-bag-shopping"></i>
            </div>
            <h3 className="font-bold text-slate-100 text-xl mb-3">E-commerce</h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Descrições de produtos que convencem, títulos que chamam atenção e bullets que vendem.
            </p>
            <div className="text-xs text-slate-500">
              <i className="fa-solid fa-check text-green-400 mr-1"></i> Descrições completas em segundos<br/>
              <i className="fa-solid fa-check text-green-400 mr-1"></i> Otimizado para conversão<br/>
              <i className="fa-solid fa-check text-green-400 mr-1"></i> Múltiplas variações para testar
            </div>
          </div>

          <div className="bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/60 p-8 hover:border-brand-500/40 transition-all">
            <div className="w-12 h-12 rounded-xl bg-brand-500/10 flex items-center justify-center text-brand-400 text-2xl mb-5">
              <i className="fa-solid fa-mobile-screen"></i>
            </div>
            <h3 className="font-bold text-slate-100 text-xl mb-3">Social Media</h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Legendas engajadoras, chamadas que param o scroll e posts que geram resultados.
            </p>
            <div className="text-xs text-slate-500">
              <i className="fa-solid fa-check text-green-400 mr-1"></i> Legendas criativas e relevantes<br/>
              <i className="fa-solid fa-check text-green-400 mr-1"></i> CTAs que geram cliques<br/>
              <i className="fa-solid fa-check text-green-400 mr-1"></i> Adaptado para cada rede social
            </div>
          </div>

          <div className="bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/60 p-8 hover:border-brand-500/40 transition-all">
            <div className="w-12 h-12 rounded-xl bg-brand-500/10 flex items-center justify-center text-brand-400 text-2xl mb-5">
              <i className="fa-solid fa-rocket"></i>
            </div>
            <h3 className="font-bold text-slate-100 text-xl mb-3">Campanhas & Ads</h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Anúncios que convertem, emails que abrem e landing pages que vendem.
            </p>
            <div className="text-xs text-slate-500">
              <i className="fa-solid fa-check text-green-400 mr-1"></i> Headlines persuasivas<br/>
              <i className="fa-solid fa-check text-green-400 mr-1"></i> Estrutura de vendas comprovada<br/>
              <i className="fa-solid fa-check text-green-400 mr-1"></i> Foco em ROI e conversão
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid gap-8 sm:grid-cols-3 text-center">
          <div>
            <div className="text-4xl font-bold text-brand-400 mb-2">10s</div>
            <div className="text-sm text-slate-400">Tempo médio de geração</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-brand-400 mb-2">100%</div>
            <div className="text-sm text-slate-400">Focado em conversão</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-brand-400 mb-2">∞</div>
            <div className="text-sm text-slate-400">Variações para testar</div>
          </div>
        </div>
      </Shell>
    </section>
  );
}

function AboutCreator(){
  return (
    <section className="py-24 bg-slate-950" aria-label="Sobre o Criador">
      <Shell>
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-50 mb-4">
            Sobre o Criador
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Desenvolvido por quem entende de tecnologia e impacto
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/60 p-8 md:p-12 hover:border-brand-500/40 transition-all">
            <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl overflow-hidden ring-4 ring-brand-500/20 shadow-xl">
                  <img
                    src="/gustavo-profile.jpg"
                    alt="Gustavo - Desenvolvedor Full-Stack"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold text-slate-50 mb-2">Gustavo</h3>
                <p className="text-brand-400 font-medium mb-4">Desenvolvedor Full-Stack | Co-fundador Mango Salad Games</p>

                <p className="text-slate-300 leading-relaxed mb-6">
                  Mais de 5 anos transformando código em soluções que democratizam acesso e resolvem problemas reais.
                  Do Laboratório de Computação Científica da UFAL à Hand Talk, sempre focado em criar tecnologia
                  que impacta pessoas. CopyGenius nasceu da mesma filosofia: <span className="text-brand-400 font-medium">ferramentas poderosas
                  devem ser acessíveis para todos</span>.
                </p>

                {/* Social Links */}
                <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-6">
                  <a
                    href="https://github.com/avoguga"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm transition-all hover:scale-105"
                  >
                    <i className="fa-brands fa-github"></i>
                    GitHub
                  </a>
                  <a
                    href="https://medium.com/@Gugass"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm transition-all hover:scale-105"
                  >
                    <i className="fa-brands fa-medium"></i>
                    Medium
                  </a>
                  <a
                    href="https://dev.to/avoguga"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm transition-all hover:scale-105"
                  >
                    <i className="fa-brands fa-dev"></i>
                    DEV
                  </a>
                </div>

                {/* Tech Stack */}
                <div className="flex flex-wrap justify-center md:justify-start gap-2">
                  {['React', 'Node.js', 'TypeScript', 'IA'].map(tech => (
                    <span key={tech} className="px-3 py-1 rounded-full bg-brand-500/10 border border-brand-500/20 text-brand-300 text-xs font-medium">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Shell>
    </section>
  );
}

const FAQ_ITEMS = [
  {
    question: 'O que é o CopyGenius?',
    answer: 'CopyGenius é uma plataforma de inteligência artificial que gera textos persuasivos (copy) para e-commerce, redes sociais e campanhas de marketing em segundos. Ideal para empreendedores, agências e profissionais de marketing que querem economizar tempo e aumentar conversões.'
  },
  {
    question: 'O CopyGenius é gratuito?',
    answer: 'Sim! Você pode começar gratuitamente sem precisar de cartão de crédito. Ao criar sua conta, você recebe créditos grátis para testar a plataforma e gerar suas primeiras copies.'
  },
  {
    question: 'Para quais plataformas o CopyGenius gera conteúdo?',
    answer: 'O CopyGenius gera conteúdo otimizado para Instagram, Facebook, TikTok, LinkedIn, Twitter/X, YouTube, Pinterest e Threads. Também cria descrições de produtos para e-commerce, headlines para anúncios e emails de marketing.'
  },
  {
    question: 'Quanto tempo leva para gerar uma copy?',
    answer: 'Em média, o CopyGenius gera textos profissionais em apenas 10 segundos. Você recebe múltiplas variações de títulos, descrições e CTAs prontas para usar.'
  },
  {
    question: 'Posso gerar imagens junto com a copy?',
    answer: 'Sim! O CopyGenius possui geração de imagens com IA integrada. Você pode criar imagens profissionais para acompanhar seus posts de redes sociais diretamente na plataforma.'
  },
  {
    question: 'Os textos gerados são únicos?',
    answer: 'Sim, todos os textos são gerados de forma única pela IA com base nas informações que você fornece sobre seu produto, serviço ou campanha. Cada geração é personalizada para o seu caso específico.'
  }
];

function FAQ(){
  return (
    <section className="py-24 bg-slate-900/30" aria-label="Perguntas Frequentes" id="faq">
      <Shell>
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-50 mb-4">
            Perguntas Frequentes
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Tire suas dúvidas sobre o CopyGenius
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {FAQ_ITEMS.map((item, index) => (
            <details
              key={index}
              className="group bg-slate-900/80 backdrop-blur-sm rounded-xl border border-slate-700/60 hover:border-brand-500/40 transition-all"
            >
              <summary className="flex items-center justify-between cursor-pointer p-6 text-left">
                <h3 className="font-semibold text-slate-100 pr-4">{item.question}</h3>
                <i className="fa-solid fa-chevron-down text-slate-400 group-open:rotate-180 transition-transform"></i>
              </summary>
              <div className="px-6 pb-6 text-slate-300 text-sm leading-relaxed">
                {item.answer}
              </div>
            </details>
          ))}
        </div>
      </Shell>
    </section>
  );
}

function CTA(){
  return (
    <section aria-label="Call to Action">
      <Shell>
      <div className="my-20 relative">
        <div className="relative rounded-3xl bg-gradient-to-br from-brand-600 via-brand-500 to-purple-600 p-12 md:p-16 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
          </div>

          <div className="relative text-center max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              Cada segundo sem copy profissional é dinheiro deixado na mesa
            </h2>
            <p className="text-lg sm:text-xl text-brand-50 mb-10 leading-relaxed">
              Comece <span className="font-bold">grátis agora</span> e crie sua primeira copy em menos de 1 minuto.
              Sem cartão, sem burocracia, sem desculpas para não vender mais.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register" className="btn h-14 px-8 text-base bg-white text-brand-600 hover:bg-slate-100 font-bold shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
                <i className="fa-solid fa-rocket"></i> Começar Grátis Agora
              </Link>
            </div>
            <div className="mt-8 flex flex-col gap-3">
              <p className="text-sm text-brand-100 font-medium">
                <i className="fa-solid fa-check mr-1"></i> Créditos grátis ao criar conta • <i className="fa-solid fa-check mr-1"></i> Cancele quando quiser • <i className="fa-solid fa-check mr-1"></i> Suporte brasileiro
              </p>
              <p className="text-xs text-brand-200/80">
                Junte-se a centenas de criadores e empresas que já aumentaram suas conversões com CopyGenius
              </p>
            </div>
          </div>
        </div>
      </div>
      </Shell>
    </section>
  );
}

function Footer(){
  return (
    <footer className="py-12 mt-auto border-t border-slate-800/60 bg-slate-950">
      <Shell>
        <div className="grid gap-8 md:grid-cols-4 mb-8">
          {/* Brand */}
          <div className="text-center md:text-left">
            <div className="font-bold text-lg text-slate-200 mb-2">CopyGenius</div>
            <p className="text-sm text-slate-400 mb-4">
              Gerador de copy com IA para e-commerce e redes sociais. Textos persuasivos em segundos.
            </p>
            <div className="flex justify-center md:justify-start gap-3">
              <a
                href="https://www.instagram.com/copygenius.ia/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-brand-500 flex items-center justify-center transition-all hover:scale-110"
                aria-label="Siga CopyGenius no Instagram"
              >
                <i className="fa-brands fa-instagram text-slate-200"></i>
              </a>
            </div>
          </div>

          {/* Product */}
          <div className="text-center md:text-left">
            <h3 className="font-semibold text-slate-300 mb-3 text-sm">Produto</h3>
            <nav className="flex flex-col gap-2 text-sm text-slate-400">
              <a href="#como-funciona" className="hover:text-brand-400 transition">Como Funciona</a>
              <a href="#funcionalidades" className="hover:text-brand-400 transition">Funcionalidades</a>
              <a href="#depoimentos" className="hover:text-brand-400 transition">Depoimentos</a>
              <a href="#faq" className="hover:text-brand-400 transition">FAQ</a>
            </nav>
          </div>

          {/* Account */}
          <div className="text-center md:text-left">
            <h3 className="font-semibold text-slate-300 mb-3 text-sm">Conta</h3>
            <nav className="flex flex-col gap-2 text-sm text-slate-400">
              <Link to="/login" className="hover:text-brand-400 transition">Fazer Login</Link>
              <Link to="/register" className="hover:text-brand-400 transition">Criar Conta Grátis</Link>
              <Link to="/forgot" className="hover:text-brand-400 transition">Recuperar Senha</Link>
            </nav>
          </div>

          {/* Support */}
          <div className="text-center md:text-left">
            <h3 className="font-semibold text-slate-300 mb-3 text-sm">Suporte</h3>
            <div className="flex flex-col gap-2 text-sm text-slate-400">
              <a
                href="mailto:avogugadev@gmail.com"
                className="hover:text-brand-400 transition inline-flex items-center gap-2"
              >
                <i className="fa-solid fa-envelope"></i>
                avogugadev@gmail.com
              </a>
              <p className="text-xs text-slate-500">Respondemos em até 24h</p>
              <a
                href="https://www.instagram.com/copygenius.ia/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-brand-400 transition inline-flex items-center gap-2"
              >
                <i className="fa-brands fa-instagram"></i>
                @copygenius.ia
              </a>
            </div>
          </div>
        </div>

        {/* SEO Keywords Footer */}
        <div className="py-6 border-t border-slate-800/60 mb-6">
          <p className="text-xs text-slate-600 text-center leading-relaxed">
            CopyGenius - Gerador de Copy com IA | Copywriting Automático | Textos para E-commerce |
            Legendas para Instagram | Copy para Facebook | Descrição de Produtos |
            Headlines Persuasivas | Marketing Digital | Inteligência Artificial para Vendas
          </p>
        </div>

        <div className="pt-6 border-t border-slate-800/60 text-center text-xs text-slate-500">
          <p>© {new Date().getFullYear()} CopyGenius • Plataforma brasileira de geração de textos com IA</p>
          <p className="mt-2 text-slate-600">
            Desenvolvido por <a href="https://github.com/avoguga" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-300 transition">Gustavo</a> •
            Feito no Brasil para empreendedores, agências e profissionais de marketing
          </p>
        </div>
      </Shell>
    </footer>
  );
}
